import { QueryClient } from "@tanstack/react-query";

/**
 * 2026-04-23 perf pass. Before this, QueryClient was bare `new QueryClient()`
 * which meant:
 *   - staleTime=0       → every remount refetches (Dashboard ↔ Orders flashed)
 *   - refetchOnWindowFocus=true → alt-tab back to the browser = burst of 20+ calls
 *   - refetchOnMount=true  → navigating pages spammed the server
 *
 * For live trading we rely on WebSocket (useLiveTicks) for realtime updates —
 * REST polls are a safety fallback. Defaults below make page switches feel
 * instant (cache hit if recently visited) while polling still refreshes data
 * on its own schedule.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Consider data fresh for 15s after fetch. Page revisits within 15s
      // hit cache instead of refetching — the single biggest page-switch win.
      staleTime: 15_000,
      // Keep unused data in memory for 10 min so tab-to-tab navigation is free.
      gcTime: 600_000,
      // Alt-tab back into the browser should NOT trigger a 20-query burst —
      // our WebSocket reconnect-on-visibility already handles staleness.
      refetchOnWindowFocus: false,
      // Component remount (e.g. React strict-mode or route change) should
      // trust the cache instead of re-requesting. Individual hooks that
      // genuinely need a fresh read on mount can override per-query.
      refetchOnMount: false,
      // If the browser briefly loses network, don't retry-storm on recovery.
      refetchOnReconnect: "always",
      // Default retry once on failure (React Query default is 3 with backoff
      // which can chain 5-10s of waiting on a flapping endpoint).
      retry: 1,
    },
  },
});
